/**
 * Main Router Configuration
 *
 * This file defines the root-level routes and imports module-specific routes.
 * All /app/* routes are centralized in modules/routes.
 */

import type { RouteObject } from 'react-router-dom';
import { getModuleRoutes, mergeRoutes } from './moduleRouter';

// Auth Pages
import Login from '../modules/core/auth/pages/Login';
import Signup from '../modules/core/auth/pages/Signup';
import ForgotPassword from '../modules/core/auth/pages/ForgotPassword';
import ResetPassword from '../modules/core/auth/pages/ResetPassword';
import SelectRole from '../modules/core/auth/pages/SelectRole';
import SelectUda from '../modules/core/auth/pages/SelectUda';

// Core Components
import NotFound from '../pages/NotFound';

// Public Pages
import { HomePage } from '../pages/home/page';
import { DocumentationPage } from '../pages/documentation/ModuleDocumentation';
import { lazy, Suspense } from 'react';

// Public Layout (Header + Footer wrapper)
import { PublicLayout } from '../components/PublicLayout';
import { MapSkeleton } from '@/components/base/Skeleton';

// Resource Pages
import { ResourcesPage } from '../pages/resources/ResourcesPage';
import { ActsBylawsPage } from '../pages/resources/ActsBylawsPage';
import { GosCircularsPage } from '../pages/resources/GosCircularsPage';
import { FaqsPage } from '../pages/resources/FaqsPage';
import { TrainingPage } from '../pages/resources/TrainingPage';

// About Pages
import { AboutPage } from '../pages/about/AboutPage';
import { FunctionsPage } from '../pages/about/FunctionsPage';
import { CitizenCharterPage } from '../pages/about/CitizenCharterPage';
import { TeamPage } from '../pages/about/TeamPage';

// GIS Map
import { GisMapPage } from '../pages/gis-map/GisMapPage';

// Other Public Pages
import { RtiPage } from '../pages/rti/RtiPage';
import { ContactPage } from '../pages/contact/ContactPage';
import { HelpDeskPage } from '../pages/helpdesk/HelpDeskPage';

// Digital Twin Viewer (public access via UUID)
const DigitalTwinViewerPage = lazy(() => import('../pages/digital-twin/DigitalTwinViewerPage'));

// Apps Pages
// import AppstorePageLayout from '../pages/apps/Appstore';
// import AppDetailPage from '../pages/apps/Appstore/AppDetailPage';

// Core Module - AppStore Admin Routes
import { appstoreAdminChildRoutes } from '../modules/core/appstore-admin/routes/config';

// ============================================================================
// Import all app routes from modules/routes (includes business & utility)
// ============================================================================
import { appRoutes } from '../modules/routes';
import PrivateRoute from '../components/PrivateRoute';
import Layout from '../components/Layout';
import { serviceRoutes } from './serviceRoutes';

// ============================================================================
// Base routes (public, auth, and non-module routes)
// ============================================================================
const baseRoutes: RouteObject[] = [
  // Home
  {
    path: '/',
    element: <HomePage />
  },
  // Documentation
  {
    path: '/documentation/:moduleId',
    element: <DocumentationPage />
  },
  // Digital Twin Viewer (public access via UUID)
  {
    path: '/digital-twin/:uuid',
    element: (
      <Suspense fallback={<div style={{ display: 'flex', height: '100vh', background: '#121212', padding: 16 }}><MapSkeleton className="border-slate-700" /></div>}>
        <DigitalTwinViewerPage />
      </Suspense>
    )
  },
  // Auth Routes
  {
    path: '/login',
    element: <Login />
  },
  {
    path: '/signup',
    element: <Signup />
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />
  },
  {
    path: '/reset-password',
    element: <ResetPassword />
  },
  {
    // First step of the sign-in cascade, for an employee who belongs to more than one UDA.
    // Runs before /select-role because the chosen UDA decides which assignments — and so
    // which roles — exist at all.
    path: '/select-uda',
    element: <SelectUda />
  },
  {
    // Shown between login and the app when the employee holds more than one role.
    // Must stay outside /app/* — the sidebar it would render is exactly the thing
    // the user has not chosen yet.
    path: '/select-role',
    element: <SelectRole />
  },
  // ========================================================================
  // Public Pages with Header/Footer (About, Resources, RTI, Contact, etc.)
  // ========================================================================
  {
    element: <PublicLayout />,
    children: [
      // About Pages
      { path: '/about', element: <AboutPage /> },
      { path: '/about/functions', element: <FunctionsPage /> },
      { path: '/about/citizen-charter', element: <CitizenCharterPage /> },
      { path: '/about/team', element: <TeamPage /> },
      // Resource Pages
      { path: '/resources', element: <ResourcesPage /> },
      { path: '/resources/acts-bylaws', element: <ActsBylawsPage /> },
      { path: '/resources/gos-circulars', element: <GosCircularsPage /> },
      { path: '/resources/faqs', element: <FaqsPage /> },
      { path: '/resources/training', element: <TrainingPage /> },
      // GIS Map
      { path: '/gis-map', element: <GisMapPage /> },
      // RTI
      { path: '/rti', element: <RtiPage /> },
      // Contact
      { path: '/contact', element: <ContactPage /> },
      // Help Desk
      { path: '/help-desk', element: <HelpDeskPage /> },
    ],
  },
  // Apps Store (public)
  // {
  //   path: '/apps/appstore',
  //   element: <Layout />,
  //   children: [
  //     {
  //       index: true,
  //       element: <AppstorePageLayout />
  //     },
  //     {
  //       path: 'app/:id',
  //       element: <AppDetailPage />
  //     }
  //   ]
  // },
  // Apps (protected)
  // ========================================================================
  // Service Routes (citizen service pages)
  // ========================================================================
  ...serviceRoutes,
  // ========================================================================
  // All App Routes (core, business & utility modules)
  // ========================================================================
  ...appRoutes,
  {
    path: '/apps',
    element: <PrivateRoute><Layout /></PrivateRoute>,
    children: [
      ...appstoreAdminChildRoutes,
    ]
  },
  // ========================================================================
  // Catch-all 404
  // ========================================================================
  {
    path: '*',
    element: <NotFound />
  }
];

// Get module routes from dynamic module system
const moduleRoutes = getModuleRoutes();

// Merge base routes with module routes
const routes: RouteObject[] = mergeRoutes(baseRoutes, moduleRoutes);

export default routes;
